import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../../../lib/supabase'
import { useAuth } from '../../../hooks/useAuth'
import { saveWithConflictCheck } from '../../../lib/conflictResolver'

export const useBudgetCategories = () => {
  const { household, user } = useAuth()
  const qc  = useQueryClient()
  const key = ['budget-categories', household?.id]

  const query = useQuery({
    queryKey: key,
    enabled:  !!household?.id,
    queryFn:  async () => {
      const { data, error } = await supabase
        .from('budget_categories')
        .select('id, name, type, monthly_goal, color')
        .eq('household_id', household.id)
        .order('type')
        .order('name')
      if (error) throw error
      return data ?? []
    },
  })

  const inv = () => qc.invalidateQueries({ queryKey: key })

  const createCategory = async (fields) => {
    const { error } = await supabase
      .from('budget_categories')
      .insert({ ...fields, household_id: household.id })
    if (error) throw error
    inv()
  }

  const updateCategory = async ({ id, localUpdatedAt, ...fields }) => {
    if (localUpdatedAt) {
      const result = await saveWithConflictCheck('budget_categories', id, fields, localUpdatedAt)
      if (result.conflict) return result
    } else {
      const { error } = await supabase.from('budget_categories').update(fields).eq('id', id)
      if (error) throw error
    }
    inv()
    return {}
  }

  const deleteCategory = async (id) => {
    const { error } = await supabase.from('budget_categories').delete().eq('id', id)
    if (error) throw error
    inv()
  }

  return { ...query, createCategory, updateCategory, deleteCategory }
}

export const useTransactions = (year, month) => {
  const { household, user } = useAuth()
  const qc  = useQueryClient()
  const key = ['transactions', household?.id, year, month]

  const start = `${year}-${String(month).padStart(2, '0')}-01`
  const end   = new Date(year, month, 0).toISOString().slice(0, 10)

  const query = useQuery({
    queryKey: key,
    enabled:  !!household?.id,
    queryFn:  async () => {
      const { data, error } = await supabase
        .from('transactions')
        .select('id, category_id, amount, description, date, created_by')
        .eq('household_id', household.id)
        .gte('date', start)
        .lte('date', end)
        .order('date', { ascending: false })
      if (error) throw error
      return data ?? []
    },
  })

  const inv = () => qc.invalidateQueries({ queryKey: key })

  const addTransaction = async (fields) => {
    const { error } = await supabase
      .from('transactions')
      .insert({ ...fields, household_id: household.id, created_by: user.id })
    if (error) throw error
    inv()
  }

  const deleteTransaction = async (id) => {
    const { error } = await supabase.from('transactions').delete().eq('id', id)
    if (error) throw error
    inv()
  }

  return { ...query, addTransaction, deleteTransaction }
}

export const useBills = () => {
  const { household, user } = useAuth()
  const qc  = useQueryClient()
  const key = ['bills', household?.id]

  const query = useQuery({
    queryKey: key,
    enabled:  !!household?.id,
    queryFn:  async () => {
      const { data, error } = await supabase
        .from('bills')
        .select('id, name, amount, due_day, frequency, last_paid_at, created_by')
        .eq('household_id', household.id)
        .order('due_day')
      if (error) throw error
      return data ?? []
    },
  })

  const inv = () => qc.invalidateQueries({ queryKey: key })

  const createBill = async (fields) => {
    const { error } = await supabase
      .from('bills')
      .insert({ ...fields, household_id: household.id, created_by: user.id })
    if (error) throw error
    inv()
  }

  const togglePaid = async (bill) => {
    const last_paid_at = bill.last_paid_at ? null : new Date().toISOString()
    const { error } = await supabase.from('bills').update({ last_paid_at }).eq('id', bill.id)
    if (error) throw error
    inv()
  }

  const deleteBill = async (id) => {
    const { error } = await supabase.from('bills').delete().eq('id', id)
    if (error) throw error
    inv()
  }

  return { ...query, createBill, togglePaid, deleteBill }
}
